import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Plus, Trash } from "lucide-react"
import { useEffect, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WidgetConfigurationFormType } from ".."

type QuickActionsProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

export const QuickActions = ({ form }: QuickActionsProps) => {
  const { t } = useTranslation()
  const languages = form.watch("languages")
  const formMessages = form.watch("quickActions")
  const [messages, setMessages] = useState<{ locale: string; text: string }[]>([])

  const convertToFormFormat = (currentMessages: typeof messages) => {
    const formatted = currentMessages.reduce<Record<string, string[]>>((a, b) => {
      if (!a[b.locale]) {
        a[b.locale] = []
      }

      a[b.locale]?.push(b.text)

      return a
    }, {})

    form.setValue("quickActions", formatted)
    form.trigger()
  }

  const onAddNewMessage = () => {
    const currentMessages = [...messages]
    currentMessages.push({ locale: languages.at(0)?.value ?? "en", text: "" })

    setMessages(currentMessages)
  }

  const onValueChange = (index: number, key: "text" | "locale", value: string) => {
    const currentMessages = [...messages]

    if (currentMessages?.[index]) {
      currentMessages[index][key] = value
    }

    setMessages(currentMessages)
    convertToFormFormat(currentMessages)
  }

  const onDeleteOption = (index: number) => {
    const currentMessages = [...messages]
    currentMessages.splice(index, 1)

    setMessages(currentMessages)
    convertToFormFormat(currentMessages)
  }

  useEffect(() => {
    const messages = Object.entries(formMessages ?? {})
      .map(
        ([locale, texts]) =>
          texts?.map((text) => ({
            locale,
            text,
          })) ?? [],
      )
      .flat()

    setMessages(messages)
  }, [formMessages])

  return (
    <div>
      {messages.length > 0 && (
        <div className="grid gap-2 mb-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
            <div className="col-span-1 lg:col-span-9">
              <Label>{t("campaigns.templates.widget.config.bubbleMessages.message")}</Label>
            </div>
            <div className="col-span-1 lg:col-span-2">
              <Label>{t("campaigns.templates.widget.config.bubbleMessages.language")}</Label>
            </div>
          </div>
          {messages.map((message, index) => (
            <div key={message.text} className="grid grid-cols-1 lg:grid-cols-12 gap-2 group/row">
              <div className="col-span-1 lg:col-span-9">
                <Input
                  onBlur={(ev) => onValueChange(index, "text", ev.target.value)}
                  defaultValue={message.text}
                />
              </div>
              <div className="col-span-1 lg:col-span-2">
                <Select
                  onValueChange={(option) => onValueChange(index, "locale", option)}
                  value={message.locale}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 flex justify-end items-center duration-100 opacity-0 group-hover/row:opacity-100">
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() => onDeleteOption(index)}
                >
                  <Trash />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Button
        variant="outline"
        onClick={onAddNewMessage}
        type="button"
        disabled={!!messages.find((e) => !e.text)}
      >
        <Plus />
        <span>{t("campaigns.templates.widget.config.bubbleMessages.addNew")}</span>
      </Button>
    </div>
  )
}
